import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { Button, Card } from '@helix-ds/react'
import { AlertBanner } from '@/components/common/AlertBanner'
import { useAuthorization } from '@/features/authorization'
import {
  acceptInvitation,
  AcceptInvitationResult,
  declineInvitation,
  InvitationError,
} from '@/features/invitations'

const HEADING = 'text-text-heading dark:text-white font-semibold tracking-tight'
const SUBTLE = 'text-sm font-medium text-text-body-subtle dark:text-gray-400'

/** Caller identity for provider-api (?caller=): email, else Auth0 subject. */
function useCaller(): string {
  const { user } = useAuth0()
  return user?.email || user?.sub || 'unknown-user'
}

/** Map an accept/decline error to a stable, user-facing message keyed on HTTP status. */
function errorMessageFor(error: unknown): string {
  if (error instanceof InvitationError) {
    if (error.status === 403) return 'This invitation is not addressed to your account.'
    if (error.status === 404) return 'This invitation was not found or has expired.'
    if (error.status === 409) return 'This invitation can no longer be accepted.'
  }
  return 'We could not process this invitation. Please try again later.'
}

type AcceptState =
  | { phase: 'LOADING' }
  | { phase: 'ACCEPTED'; result: AcceptInvitationResult }
  | { phase: 'DECLINED' }
  | { phase: 'ERROR'; message: string }

/**
 * Token-accept landing page. On mount it accepts the invitation exactly once
 * (ref-guarded against React StrictMode's double-invoke) and surfaces the
 * granted role/target for the CALLER'S OWN invite only. The raw token is used
 * solely for the API call — never rendered or logged.
 */
export default function AcceptInvitation() {
  const { token } = useParams<{ token: string }>()
  const caller = useCaller()
  const navigate = useNavigate()
  const { refreshPermissions } = useAuthorization()

  const [state, setState] = useState<AcceptState>({ phase: 'LOADING' })
  const [confirmingDecline, setConfirmingDecline] = useState(false)
  const [declining, setDeclining] = useState(false)
  const [navigating, setNavigating] = useState(false)
  // Idempotency guard: a single mount (even under StrictMode's double-invoke)
  // must fire at most one accept so one click never fires two accepts.
  const acceptedRef = useRef(false)

  useEffect(() => {
    if (acceptedRef.current) return
    acceptedRef.current = true
    if (!token) {
      setState({ phase: 'ERROR', message: errorMessageFor(new InvitationError(404, 'missing token')) })
      return
    }
    // The ref guard already guarantees a single accept per mount (even under
    // StrictMode's mount/unmount/remount). We intentionally do NOT gate the
    // setState on a cleanup flag: StrictMode's first-pass cleanup would flip
    // such a flag before the sole in-flight accept resolves, discarding the
    // result and hanging the page on the spinner. A late setState after an
    // unmount is a harmless no-op in React 18.
    void acceptInvitation(token, caller)
      .then((result) => {
        setState({ phase: 'ACCEPTED', result })
        // The accept just granted new case:* permissions server-side. The
        // AuthorizationContext still holds the caller's PRE-accept permission
        // set (which for an org-less invitee is empty — the /auth/permissions
        // call 404'd before the user existed). Without refreshing it here, the
        // case page's <PermissionGuard permission="case:view"> would deny
        // access even though the grant succeeded. Refresh in the background so
        // the new permissions are loaded by the time the user navigates.
        void refreshPermissions()
      })
      .catch((error) => {
        setState({ phase: 'ERROR', message: errorMessageFor(error) })
      })
    // caller is derived from the authenticated session and stable for this
    // mount; the accept must run once for the token, so it is intentionally not
    // re-run on caller identity churn (guarded by acceptedRef regardless).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  /**
   * Navigate to a post-accept destination, but only AFTER a fresh permission
   * load completes — so the destination's PermissionGuard sees the newly
   * granted case:* permissions rather than the stale pre-accept set.
   */
  const goAfterRefresh = async (to: string) => {
    setNavigating(true)
    try {
      await refreshPermissions()
    } finally {
      setNavigating(false)
      navigate(to)
    }
  }

  const handleDecline = async () => {
    if (!token) return
    setDeclining(true)
    try {
      await declineInvitation(token, caller)
      setState({ phase: 'DECLINED' })
    } catch (error) {
      setState({ phase: 'ERROR', message: errorMessageFor(error) })
    } finally {
      setDeclining(false)
      setConfirmingDecline(false)
    }
  }

  return (
    <main className="flex min-h-screen w-full items-center justify-center bg-background-gray p-6 dark:bg-gray-950">
      <Card className="w-full max-w-md" aria-labelledby="accept-invitation-heading">
        <h1 id="accept-invitation-heading" className={`${HEADING} text-xl`}>
          Case invitation
        </h1>

        {state.phase === 'LOADING' && (
          <div className="mt-4 flex items-center gap-2" role="status" aria-live="polite">
            <span
              className="h-4 w-4 animate-spin rounded-full border-2 border-brand-600 border-t-transparent"
              aria-hidden
            />
            <p className={SUBTLE}>Confirming your invitation…</p>
          </div>
        )}

        {state.phase === 'ACCEPTED' && (
          <div className="mt-4 flex flex-col gap-4">
            <p className="text-sm font-medium text-text-body dark:text-gray-300">You now have access to this case.</p>
            {state.result.roleKey && (
              <p className={SUBTLE}>
                Role granted: <span className="font-semibold text-text-heading dark:text-white">{state.result.roleKey}</span>
              </p>
            )}
            {state.result.targetType === 'CASE_CONNECT' && state.result.targetId ? (
              <Button
                type="button"
                variant="primary"
                size="sm"
                className="self-start rounded-full"
                disabled={navigating}
                onClick={() => void goAfterRefresh(`/cases/${state.result.targetId}`)}
              >
                {navigating ? 'Loading your access…' : 'Go to the case'}
              </Button>
            ) : (
              <Button
                type="button"
                variant="primary"
                size="sm"
                className="self-start rounded-full"
                disabled={navigating}
                onClick={() => void goAfterRefresh('/cases')}
              >
                {navigating ? 'Loading your access…' : 'Go to your cases'}
              </Button>
            )}
          </div>
        )}

        {state.phase === 'DECLINED' && (
          <div className="mt-4 flex flex-col gap-4">
            <p className="text-sm font-medium text-text-body dark:text-gray-300">Invitation declined.</p>
            <Link
              to="/cases"
              className="self-start rounded-full border border-border-base px-4 py-2 text-sm font-medium text-text-body transition-colors hover:bg-brand-600 hover:text-white focus:ring focus:ring-brand-600/20 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-brand-700"
            >
              Go to your cases
            </Link>
          </div>
        )}

        {state.phase === 'ERROR' && (
          <div className="mt-4">
            <AlertBanner variant="error">{state.message}</AlertBanner>
            <Link
              to="/cases"
              className="mt-4 inline-block rounded-full border border-border-base px-4 py-2 text-sm font-medium text-text-body transition-colors hover:bg-brand-600 hover:text-white focus:ring focus:ring-brand-600/20 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-brand-700"
            >
              Go to your cases
            </Link>
          </div>
        )}

        {/* Decline is a secondary action while the accept is in flight or after
            it fails — mutually exclusive with a successful accept/decline. */}
        {(state.phase === 'LOADING' || state.phase === 'ERROR') && (
          <div className="mt-6 border-t border-border-base pt-4 dark:border-gray-700">
            {confirmingDecline ? (
              <div className="flex flex-col gap-2">
                <p className={SUBTLE}>Decline this invitation? This cannot be undone.</p>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    disabled={declining}
                    onClick={() => void handleDecline()}
                  >
                    {declining ? 'Declining…' : 'Confirm decline'}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={declining}
                    onClick={() => setConfirmingDecline(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button type="button" variant="secondary" size="sm" onClick={() => setConfirmingDecline(true)}>
                Decline invitation
              </Button>
            )}
          </div>
        )}
      </Card>
    </main>
  )
}
